import { useMemo } from "react";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import AddBucketModal, {
  ADD_BUCKET_MODAL_ID,
} from "./components/AddBucketModal";
import FirstStpBucketingFooter from "./components/FirstStpBucketingFooter";
import { BucketRow } from "./data-model/bucketModel";
import { useBucketFilters } from "./hooks/useBucketFilters";
import { useFirstStpBucketing } from "./useFirstStpBucketing";
import {
  BUCKET_FIELD_KEYS,
  buildBucketTableHeaders,
  buildBucketTableRows,
} from "./utils/buildBucketTableData";

const TABLE_NAME = "first-stp-bucketing-table";
const ROWS_PER_PAGE = 10;

/**
 * Layout page for the First STP Bucketing configuration.
 */
const FirstStpBucketing = () => {
  const config = useFirstStpBucketing();
  const filters = useBucketFilters(config.rows);

  const tableHeaders = useMemo(() => buildBucketTableHeaders(), []);
  const tableData = useMemo(
    () => buildBucketTableRows(filters.filteredRows, config.isDirty),
    [filters.filteredRows, config.isDirty],
  );

  const handleCellChange = (
    rowData: { id: string },
    fieldKey: string,
    newValue: unknown,
  ) => {
    if (!rowData?.id) return;
    if (
      fieldKey === BUCKET_FIELD_KEYS.name ||
      fieldKey === BUCKET_FIELD_KEYS.sub_bucket
    ) {
      config.updateRow(rowData.id, {
        [fieldKey]: typeof newValue === "string" ? newValue : "",
      } as Partial<BucketRow>);
      return;
    }
    if (fieldKey === BUCKET_FIELD_KEYS.keywords) {
      config.updateRow(rowData.id, {
        keywords: Array.isArray(newValue) ? (newValue as string[]) : [],
      });
    }
  };

  return (
    <div className="first-stp-bucketing-layout flex h-full flex-col overflow-hidden">
      <div className="first-stp-bucketing-layout__header flex flex-row items-center justify-between border-b border-primaryGray-15 px-6 pb-4 pt-1 font-semibold">
        <div className="first-stp-bucketing-layout__header-title">
          <div className="font-bold">First STP Bucketing</div>
          <div className="text-small font-light text-primaryGray-7">
            Configure First STP Bucketing rules.
          </div>
        </div>
      </div>
      <div className="first-stp-bucketing-layout__content min-h-0 flex-1 overflow-y-auto px-6 pt-6">
        {config.loading ? (
          <div className="flex h-full items-center justify-center">
            <LoaderMessage message="Loading buckets…" />
          </div>
        ) : (
          <CustomTable
            tableName={TABLE_NAME}
            tableHeaders={tableHeaders}
            tableData={tableData}
            count={tableData.length}
            totalCount={config.rows.length}
            itemsPerPage={ROWS_PER_PAGE}
            pagesPerView={5}
            isFetching={false}
            placeholder="Search by bucket name"
            searchingText={filters.setSearch}
            enableSearchOnChange
            showRefreshButton
            onRefreshButton={config.refresh}
            showOtherButton
            otherButtonMeta={[
              {
                text: "Add new bucket",
                onClick: () => openModal(ADD_BUCKET_MODAL_ID),
                disabled: config.saving,
                buttonType: "primary",
                size: "medium",
              },
            ]}
            onCellValueChange={handleCellChange}
            disableHoverAndClick
          />
        )}
      </div>
      <FirstStpBucketingFooter
        dirtyCount={config.dirtyCount}
        saving={config.saving}
        onSave={config.saveChanges}
      />
      <AddBucketModal
        hasBucket={config.hasBucket}
        onConfirm={config.addBucket}
      />
    </div>
  );
};

export default FirstStpBucketing;
